/**
 * Restore a plain object written to localStorage by an earlier visit, returning null when it
 * cannot be used.
 *
 * The booking stepper persists four values and reads them all back on the next visit:
 * `confirmPrimitiveInput`, `primitiveData`, `confirmPrimitiveOutput` and `hotelDetails`. Each
 * was adopted wholesale with a bare `JSON.parse`, so a browser holding an object written by an
 * older build replaced the initialised state with a shape this build does not expect.
 *
 * That produced a blank reserve step twice. The first time it was `confirmPrimitiveInput` with
 * no `confirmRooms`; the second, after only that one had been guarded, it was `primitiveData`
 * whose `billingSummary` had no `roomsPrice`, reaching
 * `billingData.roomsPrice.slice()` in FinalBillingSummary. Both crashed during render, after
 * the reservation had already succeeded server-side.
 *
 * `isUsable` is where a caller states the fields it will actually index into. Checking only
 * those is deliberate: rejecting on an unfamiliar extra field would discard usable state every
 * time an unrelated optional field is added, which trades one bug for a subtler one.
 */
export function parseStoredObject<T>(
  raw: string | null | undefined,
  isUsable: (candidate: Record<string, unknown>) => boolean
): T | null {
  if (!raw) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Truncated or hand-edited storage. Nothing to recover.
    return null
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    Array.isArray(parsed) ||
    !isUsable(parsed as Record<string, unknown>)
  ) {
    return null
  }

  return parsed as T
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Both keys feed the same `setBillingData`, and FinalBillingSummary slices `roomsPrice` during
 * render. Shared rather than restated: the two predicates were written separately and had
 * already drifted -- one checked fields the other needed and did not have.
 */
const hasUsableBillingSummary = (candidate: Record<string, unknown>): boolean =>
  isPlainObject(candidate.billingSummary) &&
  Array.isArray(candidate.billingSummary.roomsPrice)

/**
 * `expiresAt` is read as `expiresAt.replace(' ', 'T')` for the countdown, and `paymentInfo`
 * replaces a non-optional piece of state that children read fields off.
 */
export const isUsablePrimitiveData = (
  candidate: Record<string, unknown>
): boolean =>
  hasUsableBillingSummary(candidate) &&
  typeof candidate.expiresAt === 'string' &&
  isPlainObject(candidate.paymentInfo)

/**
 * This one carries more than the billing summary, and the extra fields are dereferenced
 * without guards at index.tsx:386-390 and :792-796:
 *
 *   confirmPrimitiveOutput.paymentInfo.walletType
 *   confirmPrimitiveOutput.reservationInfo.displayProviderCode
 *   confirmPrimitiveOutput.reservationInfo.expiresAt.replace(' ', 'T')
 *
 * The first effect has no page condition, so it fires the moment a restored value lands.
 * An earlier version of this predicate checked only the billing summary, which let a stored
 * object with no `reservationInfo` through -- the same crash class this whole file exists to
 * stop, reached through the one key whose predicate was thinner than its consumers.
 */
export const isUsableConfirmPrimitiveOutput = (
  candidate: Record<string, unknown>
): boolean =>
  hasUsableBillingSummary(candidate) &&
  isPlainObject(candidate.paymentInfo) &&
  typeof candidate.paymentInfo.walletType === 'string' &&
  isPlainObject(candidate.reservationInfo) &&
  typeof candidate.reservationInfo.displayProviderCode === 'string' &&
  typeof candidate.reservationInfo.expiresAt === 'string'

/**
 * Read as `hotelDetails?.accommodationInfo?.star`, which is already optional-chained at every
 * site, so being a plain object is the whole requirement.
 */
export const isUsableHotelDetails = (): boolean => true
